import { initFlowbite, Modal } from 'flowbite';
import Croppie from 'croppie';
import TomSelect from 'tom-select';


/* ========================================================================
   NASTAVENÍ DEBUG MODU
   True = Detailní výpisy do konzole (pro vývoj).
   False = Ticho (pro produkci).
======================================================================== */
const DEBUG = true;

/**
 * Centrální logovací funkce.
 * Používáme ji místo console.log, abychom mohli výpisy globálně vypnout.
 */
const log = (msg, data = null) => {
    if (DEBUG) {
        if (data) console.log(`🔧 [UI-UTILS]: ${msg}`, data);
        else console.log(`🔧 [UI-UTILS]: ${msg}`);
    }
};

/* ========================================================================
   OVERLAY HELPERS
   Funkce pro ovládání overlaye z JavaScriptu (např. z Naja).
======================================================================== */
function getOverlay() { return document.getElementById('ajax-overlay'); }

export const showOverlay = (reason = '') => {
    log(`Zobrazuji overlay. Důvod: ${reason}`);
    getOverlay()?.classList.add('visible');
};

export const hideOverlay = () => {
    log('Skrývám overlay.');
    getOverlay()?.classList.remove('visible');
};

/* ========================================================================
   FLOWBITE WRAPPER
   Obaleno do try-catch, aby chyba v knihovně nezhroutila celý web.
======================================================================== */
export function initFlowbiteComponents() {
    try {
        if (typeof initFlowbite === 'function') {
            initFlowbite();
            log('Flowbite inicializováno.');
        }
    } catch (e) {
        console.error('❌ Flowbite chyba:', e);
    }
}


/* ========================================================================
   FLOWBITE MODAL AJAX BRIDGE
   Flowbite zůstává autorita pro modaly. Tahle vrstva pouze sjednocuje
   inicializaci, úklid backdropu a zavírání/otevírání po Naja AJAXu.
======================================================================== */

const flowbiteModalOptions = {
    placement: 'center',
    backdrop: 'dynamic',
    backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40',
    closable: true,
};

const normalizeFlowbiteModalId = (modalId) => {
    if (!modalId) return null;

    return String(modalId).replace(/^#/, '');
};

function cleanupFlowbiteModalState() {
    document.body.classList.remove('overflow-hidden');

    document
        .querySelectorAll('[modal-backdrop], .modal-backdrop')
        .forEach(backdrop => backdrop.remove());
}

function getFlowbiteModalElement(modalId) {
    const id = normalizeFlowbiteModalId(modalId);

    return id ? document.getElementById(id) : null;
}

function getFlowbiteModalInstance(modalId) {
    const modalElement = getFlowbiteModalElement(modalId);

    if (!modalElement?.id) {
        return null;
    }

    try {
        if (window.FlowbiteInstances?.getInstance) {
            const existingInstance = window.FlowbiteInstances.getInstance('Modal', modalElement.id);

            if (existingInstance) {
                return existingInstance;
            }
        }
    } catch (e) {
        // Instance manager nemusí být dostupný podle způsobu buildu Flowbite.
    }

    try {
        return new Modal(
            modalElement,
            {
                ...flowbiteModalOptions,
                onHide: () => {
                    setTimeout(cleanupFlowbiteModalState, 50);
                },
            },
            {
                id: modalElement.id,
                override: true,
            }
        );
    } catch (e) {
        console.error('❌ Flowbite modal nejde inicializovat:', modalElement.id, e);
        return null;
    }
}

export function hideFlowbiteModal(modalId) {
    const modalElement = getFlowbiteModalElement(modalId);
    const instance = getFlowbiteModalInstance(modalId);

    try {
        if (instance) {
            instance.hide();
        } else if (modalElement) {
            modalElement.classList.add('hidden');
            modalElement.setAttribute('aria-hidden', 'true');
            modalElement.removeAttribute('aria-modal');
            modalElement.removeAttribute('role');
        }
    } catch (e) {
        console.warn('Flowbite modal hide fallback:', modalId, e);

        if (modalElement) {
            modalElement.classList.add('hidden');
            modalElement.setAttribute('aria-hidden', 'true');
            modalElement.removeAttribute('aria-modal');
            modalElement.removeAttribute('role');
        }
    }

    setTimeout(cleanupFlowbiteModalState, 50);
}

export function showFlowbiteModal(modalId) {
    const modalElement = getFlowbiteModalElement(modalId);
    const instance = getFlowbiteModalInstance(modalId);

    try {
        if (instance) {
            instance.show();
            return;
        }

        if (modalElement) {
            modalElement.classList.remove('hidden');
            modalElement.setAttribute('aria-modal', 'true');
            modalElement.setAttribute('role', 'dialog');
            modalElement.removeAttribute('aria-hidden');
        }
    } catch (e) {
        console.warn('Flowbite modal show fallback:', modalId, e);

        if (modalElement) {
            modalElement.classList.remove('hidden');
        }
    }
}


function closeOtherFlowbiteModals(exceptModalId = null) {
    const exceptId = normalizeFlowbiteModalId(exceptModalId);

    document.querySelectorAll('[id][tabindex="-1"]').forEach(modalElement => {
        if (!modalElement.id || modalElement.id === exceptId) return;

        if (!modalElement.classList.contains('hidden')) {
            hideFlowbiteModal(modalElement.id);
        }
    });
}

function setupFlowbiteModalClickBridge() {
    if (window.__flowbiteModalClickBridgeReady) {
        return;
    }

    window.__flowbiteModalClickBridgeReady = true;

    document.addEventListener('click', (event) => {
        const hideTrigger = event.target.closest('[data-modal-hide]');

        if (hideTrigger) {
            const modalId = hideTrigger.dataset.modalHide;

            if (modalId) {
                event.preventDefault();
                event.stopImmediatePropagation();
                hideFlowbiteModal(modalId);
                return;
            }
        }

        const toggleTrigger = event.target.closest('[data-modal-toggle], [data-modal-target]');

        if (toggleTrigger) {
            const modalId = toggleTrigger.dataset.modalToggle || toggleTrigger.dataset.modalTarget;

            if (modalId) {
                event.preventDefault();
                event.stopImmediatePropagation();

                const beforeShowEvent = new CustomEvent('app:flowbite-modal:before-show', {
                    cancelable: true,
                    detail: {
                        modalId,
                        trigger: toggleTrigger,
                    },
                });

                document.dispatchEvent(beforeShowEvent);

                if (beforeShowEvent.defaultPrevented) {
                    return;
                }

                closeOtherFlowbiteModals(modalId);
                showFlowbiteModal(modalId);
            }
        }
    }, true);
}

function rememberSubmittingFlowbiteModal(form) {
    const modalElement = form.closest('[id][tabindex="-1"]');

    if (modalElement?.id) {
        window.__lastSubmittingFlowbiteModal = modalElement.id;
    }
}

export function setupFlowbiteModalAjaxBridge(root = document) {
    const scope = root && root.querySelectorAll ? root : document;

    setupFlowbiteModalClickBridge();

    scope.querySelectorAll('[id][tabindex="-1"]').forEach(modalElement => {
        if (!modalElement.id) return;

        const hasFlowbiteModalTrigger = document.querySelector(
            `[data-modal-target="${CSS.escape(modalElement.id)}"], [data-modal-toggle="${CSS.escape(modalElement.id)}"], [data-modal-hide="${CSS.escape(modalElement.id)}"]`
        );

        if (hasFlowbiteModalTrigger) {
            getFlowbiteModalInstance(modalElement.id);
        }
    });

    if (!window.__flowbiteModalSubmitReady) {
        window.__flowbiteModalSubmitReady = true;

        document.addEventListener('submit', (event) => {
            const form = event.target;

            if (form instanceof HTMLFormElement) {
                rememberSubmittingFlowbiteModal(form);
            }
        }, true);
    }

    if (!window.naja || window.__flowbiteModalAjaxBridgeReady) {
        return;
    }

    window.__flowbiteModalAjaxBridgeReady = true;

    window.naja.addEventListener('success', (event) => {
        const payload = event.detail?.payload || event.detail?.response || {};

        const closeModal = payload.closeModal ?? payload.close_modal ?? null;
        const openModal = payload.openModal ?? payload.open_modal ?? null;

        if (closeModal === true) {
            if (window.__lastSubmittingFlowbiteModal) {
                hideFlowbiteModal(window.__lastSubmittingFlowbiteModal);
                window.__lastSubmittingFlowbiteModal = null;
            }
        } else if (typeof closeModal === 'string') {
            hideFlowbiteModal(closeModal);
            window.__lastSubmittingFlowbiteModal = null;
        } else if (Array.isArray(closeModal)) {
            closeModal.forEach(modalId => hideFlowbiteModal(modalId));
            window.__lastSubmittingFlowbiteModal = null;
        }

        if (typeof openModal === 'string') {
            setTimeout(() => {
                initFlowbiteComponents();
                showFlowbiteModal(openModal);
            }, 80);
        }
    });

    window.naja.addEventListener('error', () => {
        cleanupFlowbiteModalState();
    });
}

/* ========================================================================
   CROPPIE LOGIKA (Ořezávání obrázků)
   Tato funkce je "chytrá" - pokud nenajde prvky, okamžitě skončí.
======================================================================== */

let croppieInstance = null;

export function destroyCroppie() {
    if (croppieInstance) {
        croppieInstance.destroy();
        croppieInstance = null;
    }
}

function getCroppieSizes(croppieElement) {
    const wrapper = croppieElement.closest('[data-croppie-wrapper]') || croppieElement.parentElement;
    const availableWidth = wrapper ? wrapper.clientWidth : 300;

    const boundaryWidth = Math.max(220, Math.min(availableWidth, 280));
    const boundaryHeight = Math.max(230, Math.min(boundaryWidth, 270));

    const viewportSize = Math.max(165, Math.min(boundaryWidth - 48, 210));

    return {
        boundaryWidth,
        boundaryHeight,
        viewportSize,
    };
}

export function setupCroppieFlow(root = document) {
    const el = {
        container: root.querySelector('#croppie-container'),
        interaction: root.querySelector('#croppie-interaction'),
        croppie: root.querySelector('[data-croppie]'),
        input: root.querySelector('input[type="file"][name="image"]'),
        form: root.querySelector('input[type="file"][name="image"]')?.closest('form'),
        origAvatar: root.querySelector('#avatar-container'),
        inputAvatar: root.querySelector('#input-file-avatar'),
        inputAvatarBtn: root.querySelector('#input-file-avatar-btn'),
        saveBtn: root.querySelector('#saveCrop'),
        cancelBtn: root.querySelector('#cancel-crop'),
        error: root.querySelector('[data-avatar-error]'),
    };

    if (!el.container || !el.croppie || !el.input || !el.form) return;
    if (el.input.dataset.croppieReady) return;

    el.input.dataset.croppieReady = 'true';

    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    const maxBytes = Number(el.input.dataset.avatarMaxBytes || 10_000_000);

    const setError = message => {
        if (!el.error) return;
        el.error.textContent = message || '';
        el.error.classList.toggle('hidden', !message);
    };

    const restoreUploadState = () => {
        destroyCroppie();
        el.container.classList.add('hidden');
        el.container.style.display = '';
        el.interaction.classList.add('hidden');
        el.interaction.style.display = '';
        if (el.origAvatar) el.origAvatar.style.display = '';
        if (el.inputAvatar) el.inputAvatar.style.display = '';
        if (el.inputAvatarBtn) el.inputAvatarBtn.style.display = '';
    };

    el.input.addEventListener('change', e => {
        const file = e.target.files?.[0];
        setError('');

        if (!file) {
            restoreUploadState();
            return;
        }

        if (!allowedTypes.has(file.type)) {
            setError('Vyber JPG, PNG nebo WEBP obrázek.');
            el.input.value = '';
            return;
        }

        if (file.size > maxBytes) {
            setError(`Obrázek je příliš velký. Maximum je ${Math.round(maxBytes / 1_000_000)} MB.`);
            el.input.value = '';
            return;
        }

        const reader = new FileReader();

        reader.onerror = () => {
            setError('Obrázek se nepodařilo načíst. Zkus jiný soubor.');
            el.input.value = '';
        };

        reader.onload = ev => {
            if (el.origAvatar) el.origAvatar.style.display = 'none';
            if (el.inputAvatar) el.inputAvatar.style.display = 'none';
            if (el.inputAvatarBtn) el.inputAvatarBtn.style.display = 'none';

            el.container.classList.remove('hidden');
            el.container.style.display = 'block';
            el.interaction.classList.remove('hidden');
            el.interaction.style.display = 'flex';

            destroyCroppie();

            requestAnimationFrame(() => {
                const sizes = getCroppieSizes(el.croppie);
                el.croppie.innerHTML = '';

                croppieInstance = new Croppie(el.croppie, {
                    viewport: {
                        width: sizes.viewportSize,
                        height: sizes.viewportSize,
                        type: 'square',
                    },
                    boundary: {
                        width: sizes.boundaryWidth,
                        height: sizes.boundaryHeight,
                    },
                    enableExif: true,
                    enableOrientation: true,
                    showZoomer: true,
                    mouseWheelZoom: 'ctrl',
                });

                croppieInstance.bind({ url: ev.target.result, zoom: 0 });
            });
        };

        reader.readAsDataURL(file);
    });

    el.saveBtn?.addEventListener('click', async e => {
        if (!croppieInstance) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        setError('');

        el.saveBtn.disabled = true;
        el.saveBtn.classList.add('opacity-60', 'pointer-events-none');

        try {
            const base64 = await croppieInstance.result({
                type: 'base64',
                size: { width: 512, height: 512 },
                format: 'png',
                quality: 1,
            });

            if (window.naja) {
                const formData = new FormData(el.form);
                formData.set('croppedImage', base64);

                // Stejně jako v Clasico: spoléháme na standardní Nette ID frm-<component>.
                const formName = el.form.id.replace(/^frm-/, '');
                const signal = `${formName}-send`;
                const url = `${window.location.pathname}?do=${signal}`;

                await window.naja.makeRequest('POST', url, formData, {
                    history: false,
                    fetch: { headers: { 'X-Requested-With': 'XMLHttpRequest' } },
                });
                return;
            }

            let hidden = el.form.querySelector('input[name="croppedImage"]');
            if (!hidden) {
                hidden = document.createElement('input');
                hidden.type = 'hidden';
                hidden.name = 'croppedImage';
                el.form.appendChild(hidden);
            }
            hidden.value = base64;
            el.form.method = 'post';
            el.form.submit();
        } catch (error) {
            console.error(error);
            setError('Ořez se nepodařilo uložit. Zkus obrázek vybrat znovu.');
        } finally {
            if (document.contains(el.saveBtn)) {
                el.saveBtn.disabled = false;
                el.saveBtn.classList.remove('opacity-60', 'pointer-events-none');
            }
        }
    });

    el.cancelBtn?.addEventListener('click', e => {
        e.preventDefault();
        restoreUploadState();
        el.input.value = '';
        setError('');
    });
}

/* ========================================================================
   POKROČILÁ OVERLAY LOGIKA
   Doplňuje inline JS o chytrost (odesílání tlačítek ve formuláři).
======================================================================== */
export function setupGlobalOverlayLogic() {
    log('Aktivuji pokročilou logiku pro Overlay (form submits).');

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-overlay]');

        // Pokud to není overlay prvek nebo je to AJAX (ten řeší Naja), ignorujeme
        if (!trigger || trigger.classList.contains('ajax')) return;

        // Řešíme pouze tlačítka typu Submit
        if (trigger.tagName === 'BUTTON') {
            if (trigger.type === 'button') return; // Ignorovat type="button"

            // Ignorovat UI interakce (aby overlay neblikal při přepínání tabů)
            if (trigger.matches('[data-tabs-target], [data-modal-target], [data-toggle]')) {
                hideOverlay();
                return;
            }

            const form = trigger.closest('form');
            if (form && (!trigger.type || trigger.type === 'submit')) {
                // Validace formuláře (required pole atd.)
                if (!form.checkValidity()) {
                    log('⚠️ Formulář není validní -> Overlay ruším.');
                    hideOverlay();
                    return;
                }

                showOverlay('Formulář Submit');
                event.preventDefault();

                // Malé zpoždění pro vykreslení overlaye
                setTimeout(() => {
                    // Pokud tlačítko má 'name', musíme ho poslat jako hidden input
                    // Jinak by Nette nevědělo, které tlačítko bylo stisknuto
                    if (trigger.name && !form.querySelector(`input[name="${trigger.name}"]`)) {
                        log(`➕ Přidávám hidden input: ${trigger.name}`);
                        const i = document.createElement('input');
                        i.type = 'hidden';
                        i.name = trigger.name;
                        i.value = trigger.value || '';
                        form.appendChild(i);
                    }
                    form.submit();
                }, 50);
            }
        }
    });
}

/* ========================================================================
   LEGACY SNIPPETS
   Podpora pro starší kód (funkce definované globálně ve window).
======================================================================== */
export function initLegacySnippets(root = document) {
    const legacyFuncs = [
        'initSearchInput',
        'initTinyMCE',
        'initClipboardCopy',
        'initClipboardRead',
        'initBootstrapUI'
    ];

    legacyFuncs.forEach(fn => {
        try {
            if (typeof window[fn] === 'function') {
                // log(`Spouštím legacy funkci: ${fn}`);
                window[fn](root);
            }
        } catch (e) { console.error(`Chyba v legacy funkci ${fn}`, e); }
    });
}


export function initTomSelect(root = document, selectors = ['.ts-field', '[data-tom-select="1"]']) {
    const scope = root && root.querySelectorAll ? root : document;
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    const processedElements = new Set();
    let initializedCount = 0;

    const resolveAjaxUrl = (element, value, tomSelectInstance = null) => {
        if (!value) {
            return null;
        }

        /**
         * 1) Nejlepší varianta:
         * <option value="128" data-url="...">Uživatel</option>
         */
        const selectedNativeOption =
            element.selectedOptions?.[0]
            || element.querySelector(`option[value="${CSS.escape(String(value))}"]`);

        const optionUrl =
            selectedNativeOption?.dataset?.url
            || selectedNativeOption?.dataset?.href
            || null;

        if (optionUrl) {
            return optionUrl;
        }

        /**
         * 2) Fallback přes TomSelect option element.
         */
        if (tomSelectInstance) {
            const tsOption = tomSelectInstance.getOption(value);

            const tsOptionUrl =
                tsOption?.dataset?.url
                || tsOption?.dataset?.href
                || null;

            if (tsOptionUrl) {
                return tsOptionUrl;
            }
        }

        /**
         * 3) Fallback přes šablonu:
         * data-ajax-url-template="/admin/role?do=selectUser&id=__VALUE__"
         */
        const template =
            element.dataset.ajaxUrlTemplate
            || element.dataset.urlTemplate
            || null;

        if (template) {
            return template
                .replace('__VALUE__', encodeURIComponent(value))
                .replace('%value%', encodeURIComponent(value))
                .replace(':value', encodeURIComponent(value));
        }

        /**
         * 4) Fallback přes base URL:
         * data-ajax-url="/admin/role?do=selectUser&id="
         */
        const baseUrl =
            element.dataset.ajaxUrl
            || element.dataset.url
            || null;

        if (baseUrl) {
            return `${baseUrl}${encodeURIComponent(value)}`;
        }

        return null;
    };

    const triggerAjaxChange = (element, value, tomSelectInstance = null) => {
        if (element.dataset.ajaxChange !== '1') {
            return;
        }

        const url = resolveAjaxUrl(element, value, tomSelectInstance);

        if (!url) {
            console.warn('TomSelect AJAX change: chybí URL pro vybranou hodnotu.', {
                element,
                value,
            });
            return;
        }

        const useHistory = element.dataset.ajaxHistory === '1';

        if (window.naja) {
            window.naja.makeRequest('GET', url, null, {
                history: useHistory,
                fetch: {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                },
            });

            return;
        }

        window.location.href = url;
    };

    for (const selector of selectorList) {
        const elements = scope.querySelectorAll(selector);

        for (const element of elements) {
            if (!element || processedElements.has(element)) continue;
            if (!(element instanceof HTMLSelectElement)) continue;

            processedElements.add(element);

            /**
             * Pokud už TomSelect existuje, jen případně dopojíme AJAX change.
             * To je důležité po AJAX redrawu / při opakované inicializaci.
             */
            if (element.tomselect) {
                if (element.dataset.ajaxChange === '1' && element.dataset.tomSelectAjaxReady !== '1') {
                    element.dataset.tomSelectAjaxReady = '1';

                    element.tomselect.on('change', (value) => {
                        triggerAjaxChange(element, value, element.tomselect);
                    });
                }

                continue;
            }

            if (element.dataset.tomSelectReady === '1') {
                continue;
            }

            element.dataset.tomSelectReady = '1';

            const maxItems = element.dataset.maxItems
                ? Number.parseInt(element.dataset.maxItems, 10)
                : 1;

            const maxOptions = element.dataset.maxOptions
                ? Number.parseInt(element.dataset.maxOptions, 10)
                : null;

            const plugins = [];

            if (element.dataset.clearable === '1') {
                plugins.push('clear_button');
            }

            new TomSelect(element, {
                create: false,
                maxItems: Number.isFinite(maxItems) && maxItems > 0 ? maxItems : 1,
                maxOptions: Number.isFinite(maxOptions) && maxOptions > 0 ? maxOptions : null,
                closeAfterSelect: true,
                plugins,

                sortField: {
                    field: 'text',
                    direction: 'asc',
                },

                onChange(value) {
                    triggerAjaxChange(element, value, this);
                },

                onItemAdd() {
                    this.blur();
                },

                onDropdownClose() {
                    this.blur();
                },
            });

            if (element.dataset.ajaxChange === '1') {
                element.dataset.tomSelectAjaxReady = '1';
            }

            initializedCount++;
        }
    }

    return initializedCount > 0;
}

let dayScrollNavCleanup = null;

export function initStickyDayNav(root = document) {
    const navLinks = Array.from(root.querySelectorAll('[data-day-nav]'));
    const sections = Array.from(root.querySelectorAll('[data-day-section]'));

    // Když na stránce sticky navigace dnů není, nic nedělej
    if (!navLinks.length || !sections.length) {
        if (typeof dayScrollNavCleanup === 'function') {
            dayScrollNavCleanup();
            dayScrollNavCleanup = null;
        }
        return false;
    }

    // Při reinitu po AJAXu odpoj staré listenery
    if (typeof dayScrollNavCleanup === 'function') {
        dayScrollNavCleanup();
        dayScrollNavCleanup = null;
    }

    const activeClasses = ['bg-[#C8522A]', 'text-white'];
    const inactiveClasses = ['bg-gray-100', 'text-gray-700', 'dark:bg-gray-800', 'dark:text-gray-200'];

    const setActiveLink = (id, scrollNav = false) => {
        navLinks.forEach(link => {
            const isActive = link.dataset.dayNav === id;

            activeClasses.forEach(cls => link.classList.toggle(cls, isActive));
            inactiveClasses.forEach(cls => link.classList.toggle(cls, !isActive));
        });

        if (scrollNav) {
            const activeLink = navLinks.find(link => link.dataset.dayNav === id);
            if (activeLink) {
                activeLink.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest'
                });
            }
        }
    };

    const getStickyOffset = () => {
        const stickyWrapper =
            document.querySelector('#day-scroll-nav')?.closest('.sticky') ||
            root.querySelector('#day-scroll-nav')?.closest('.sticky');

        const stickyHeight = stickyWrapper ? stickyWrapper.getBoundingClientRect().height : 0;

        // základní offset pro header + sticky nav
        return 80 + stickyHeight;
    };

    const findActiveSection = () => {
        const offset = getStickyOffset();
        let activeId = sections[0]?.dataset.daySection ?? null;
        let bestTop = -Infinity;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            const top = rect.top - offset;

            if (top <= 0 && top > bestTop) {
                bestTop = top;
                activeId = section.dataset.daySection;
            }
        });

        if (!activeId) {
            let nearestId = null;
            let nearestDistance = Infinity;

            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                const distance = Math.abs(rect.top - offset);

                if (distance < nearestDistance) {
                    nearestDistance = distance;
                    nearestId = section.dataset.daySection;
                }
            });

            activeId = nearestId;
        }

        return activeId;
    };

    let ticking = false;

    const updateActiveOnScroll = () => {
        if (ticking) return;

        ticking = true;

        requestAnimationFrame(() => {
            const activeId = findActiveSection();
            if (activeId) {
                setActiveLink(activeId, false);
            }
            ticking = false;
        });
    };

    const clickHandlers = [];

    navLinks.forEach(link => {
        const handler = (e) => {
            e.preventDefault();

            const id = link.dataset.dayNav;
            const target = document.getElementById(id);

            if (!target) return;

            setActiveLink(id, true);

            const offset = getStickyOffset();
            const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;

            window.scrollTo({
                top: targetTop,
                behavior: 'smooth'
            });
        };

        link.addEventListener('click', handler);
        clickHandlers.push({ link, handler });
    });

    const onScroll = () => updateActiveOnScroll();
    const onResize = () => updateActiveOnScroll();
    const onOrientationChange = () => updateActiveOnScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);

    updateActiveOnScroll();
    setTimeout(updateActiveOnScroll, 120);
    setTimeout(updateActiveOnScroll, 400);

    dayScrollNavCleanup = () => {
        clickHandlers.forEach(({ link, handler }) => {
            link.removeEventListener('click', handler);
        });

        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onOrientationChange);
    };

    return true;
}